import { deriveMarginReportEntry } from './derive'
import { MarginReportChartInput } from './types'

export type ColumnId =
  | 'totalCreditAmount'
  | 'deposits'
  | 'requiredMargin'
  | 'initialMargin'
  | 'unrealizedAmount'
  | 'realizedAmount'
  | 'materializedUnsettledAmount'
  | 'surplusShortage'
  | 'maxTradableAmount'
  | 'tradableRemaining'

export type ValueEmphasisKind = 'bold' | 'signed'

// component.json の `component:features.margin_report.table.*` に対応するキー（`table.` は含まない）。
// リテラルユニオンにしておくことで、component 側の t(`...table.${labelKey}`) が
// i18next の厳密なキー型チェックを通る。
export type ColumnLabelKey =
  | 'total_credit_amount'
  | 'deposits'
  | 'required_margin'
  | 'initial_margin'
  | 'unrealized_amount'
  | 'realized_amount'
  | 'mtm'
  | 'surplus'
  | 'max_tradable_amount'
  | 'tradable_remaining'

export type ColumnConfig = {
  id: ColumnId
  // ハイブリッド（保証発行上限あり）のときだけ表示する列
  hybridOnly?: boolean
  valueEmphasis?: ValueEmphasisKind
  labelKey: ColumnLabelKey
}

// 列構成・列順（画面表示・CSV出力で共用する唯一の定義）。
// ハイブリッド時: 日付 + 以下10列。非ハイブリッド時: hybridOnly を除いた7列。
export const COLUMN_CONFIGS: readonly ColumnConfig[] = [
  {
    id: 'totalCreditAmount',
    hybridOnly: true,
    valueEmphasis: 'bold',
    labelKey: 'total_credit_amount',
  },
  { id: 'deposits', labelKey: 'deposits' },
  { id: 'requiredMargin', labelKey: 'required_margin' },
  { id: 'initialMargin', labelKey: 'initial_margin' },
  { id: 'unrealizedAmount', labelKey: 'unrealized_amount' },
  { id: 'realizedAmount', labelKey: 'realized_amount' },
  { id: 'materializedUnsettledAmount', labelKey: 'mtm' },
  { id: 'surplusShortage', valueEmphasis: 'signed', labelKey: 'surplus' },
  {
    id: 'maxTradableAmount',
    hybridOnly: true,
    labelKey: 'max_tradable_amount',
  },
  // 取引可能残枠の負値装飾はデザイン未定の仮置き（ECLEAR-5016 デザイナー確認事項 U-04）。
  // 余剰/不足と同じ signed 装飾を流用する。
  {
    id: 'tradableRemaining',
    hybridOnly: true,
    valueEmphasis: 'signed',
    labelKey: 'tradable_remaining',
  },
] as const

export function getVisibleColumnConfigs(
  isHybrid: boolean,
): readonly ColumnConfig[] {
  return COLUMN_CONFIGS.filter((c) => isHybrid || c.hybridOnly !== true)
}

export type MarginReportTableRow = {
  date: string
  deposits: number | null
  totalCreditAmount: number | null
  requiredMargin: number | null
  initialMargin: number | null
  unrealizedAmount: number | null
  realizedAmount: number | null
  materializedUnsettledAmount: number | null
  surplusShortage: number | null
  maxTradableAmount: number | null
  tradableRemaining: number | null
  // 画面には出さないが、行の不足判定（背景色）に使う
  shortage: number | null
}

/**
 * data（日付昇順）から表示用の行データを組み立てる。
 * derive.ts の計算は行ごとに一度だけ呼び出し、結果をそのまま合成する。
 * 画面表示は日付の降順（最新が最上行）のため、最後に反転する。
 * CSV 出力もこの結果をそのまま使うことで画面と行順を一致させる。
 */
export function buildMarginReportTableRows(
  data: readonly MarginReportChartInput[],
  isHybrid: boolean,
): MarginReportTableRow[] {
  const ascending = data.map((d) => {
    const derived = deriveMarginReportEntry(d, isHybrid)
    return {
      date: d.date,
      deposits: d.deposits,
      totalCreditAmount: d.totalCreditAmount,
      requiredMargin: d.requiredMargin,
      initialMargin: d.initialMargin,
      unrealizedAmount: d.unrealizedAmount,
      realizedAmount: d.realizedAmount,
      materializedUnsettledAmount: d.materializedUnsettledAmount,
      surplusShortage: derived.surplusShortage,
      maxTradableAmount: derived.maxTradableAmount,
      tradableRemaining: derived.tradableRemaining,
      shortage: derived.shortage,
    }
  })

  return ascending.reverse()
}

// 行を不足として強調するかどうかを判定する（行の背景色にのみ使う）。
export function isShortfallRow(row: MarginReportTableRow): boolean {
  return row.shortage !== null && row.shortage > 0
}

export type MarginReportCsvRow = Record<'date' | ColumnId, string>

function toStr(v: number | null): string {
  return v !== null ? String(Math.round(v)) : '-'
}

/**
 * 画面表示と同じ行順・列順で CSV 出力用の行データを組み立てる。
 * visibleColumnConfigs（画面表示に使ったものと同じ配列）で columnOrder を回すことで、
 * ハイブリッド分岐・列順の情報源を COLUMN_CONFIGS 一箇所に保つ。
 */
export function buildMarginReportCsvRows(
  rows: readonly MarginReportTableRow[],
  visibleColumnConfigs: readonly ColumnConfig[],
  formatDate: (isoDate: string) => string,
): MarginReportCsvRow[] {
  return rows.map((row) => {
    const csvRow = { date: formatDate(row.date) } as MarginReportCsvRow
    for (const config of visibleColumnConfigs) {
      csvRow[config.id] = toStr(row[config.id])
    }
    return csvRow
  })
}
